import {DataTable} from "../DataTable/DataTable";
import {ObjectMapping, useDeleteObjectMappingMutation} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {ActionIcon, Group, Text} from "@mantine/core";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useNavigate} from "react-router-dom";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {modals} from "@mantine/modals";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {usePagination} from "../DataTable/usePagination";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

const EMPTY_ARRAY: ObjectMapping[] = []

export type ObjectMappingDataTableProps = EntityDataTableProps<ObjectMapping>

export const ObjectMappingDataTable = (props: ObjectMappingDataTableProps) => {
  const {
    records: mappings = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: mappings,
    sortStatus: {columnAccessor: 'kind', direction: 'asc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const [deleteObjectMapping, {isLoading: isDeleting}] = useDeleteObjectMappingMutation();

  useEffect(() => {
    if (isDeleting) {
      notifications.show({
        id: "objectMappingDataTable_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.objectmapping", "Object Mapping"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("objectMappingDataTable_deleting")
    }
  }, [isDeleting, t]);

  const onEditClick = useCallback((objectMapping: ObjectMapping) => {
    navigate("/object-mappings/form", {
      state: {
        id: objectMapping.id,
      }
    });
  }, [navigate])

  const onDelete = useCallback((objectMapping: ObjectMapping) => {
    deleteObjectMapping(objectMapping)
      .unwrap()
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.objectmapping", "Object Mapping") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      );
  }, [deleteObjectMapping, t])

  const onDeleteClick = useCallback((objectMapping: ObjectMapping) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.objectmapping", "Object Mapping"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.objectmapping.confirm", "Are you sure you want to delete this object mapping?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        onDelete(objectMapping)
      },
    })
  }, [onDelete, t]);

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          title: t("table.entity.objectmapping.column.kind", "Kind"),
          accessor: "kind",
          width: 50,
          render: ({kind}) => <FormattedDatabaseCode value={kind} />
        },
        {
          title: t("table.entity.objectmapping.column.key", "Key"),
          accessor: "key",
          width: 100,
        },
        {
          title: t("table.entity.objectmapping.column.value", "Value"),
          accessor: "value",
          width: 100
        },
        {
          title: '',
          accessor: "actions",
          width: 100,
          textAlignment: 'right',
          render: (objectMapping) => (
            <HasAccess permissions={["edit"]}>
              <Group spacing={4} position="right" noWrap>
                <ActionIcon onClick={() => onEditClick(objectMapping)}>
                  <IconEdit size={16}/>
                </ActionIcon>
                <ActionIcon color="red" onClick={() => onDeleteClick(objectMapping)}>
                  <IconTrash size={16}/>
                </ActionIcon>
              </Group>
            </HasAccess>
          )
        }
      ]}
    />
  );
};
